import {
  IBreadcrumbStyles,
  IImageStyles,
  IMessageBarStyles,
  IPivotStyles,
  IRawStyle,
  IStackSlot,
  IStackSlots,
  IStackTokens,
  IStyle,
  mergeStyleSets
} from "@fluentui/react";
import { tokens, typographyStyles } from "@fluentui/tokens";
import React from "react";

export const PRODUCT_VIEW_STYLES = {
  PRODUCT_LIST: {
    CLASS_NAMES: mergeStyleSets({
      abbrCurrentValue: { fontWeight: tokens.fontWeightSemibold, textAlign: "center" },
      textCenter: {
        maxWidth: "100%",
        overflow: "hidden",
        textAlign: "center",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
      },
      aiSummaryCardDescription: { textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" } as IStyle,
      selectedFamilyShimmerContainer: { margin: tokens.spacingVerticalL },
      footerBadge: { cursor: "pointer" }
    }),
    CSS_STYLES: {
      shimmerStack: { root: { justifyContent: "space-evenly", height: 70 } } as IStackSlot,
      cardStyles: {
        borderRadius: tokens.spacingVerticalXXL,
        boxShadow: tokens.shadow4Brand,
        gap: tokens.spacingVerticalXL,
        height: "100%",
        padding: tokens.spacingVerticalXXL
      },
      cardHeader: {
        ...typographyStyles.subtitle2,
        color: tokens.colorNeutralForeground1,
        paddingBlockEnd: tokens.spacingVerticalL
      },
      cardHeaderDescription: {
        root: {
          display: "grid",
          width: "100%",
          gridTemplateColumns: "repeat(3, minmax(20px, 1fr))",
          gridTemplateRows: "repeat(2, minmax(15px, 1fr))",
          justifyItems: "center",
          alignItems: "center",
          ...typographyStyles.body1
        }
      } as IStackSlot,
      familyToggleButtons: {
        inner: {
          gridRowGap: "5px",
          gridColumnGap: "16px",
          gridTemplateColumns: "repeat(8, auto)",
          marginBlockEnd: tokens.spacingVerticalL
        }
      } as IStackSlots,
      nestedTabsContainer: {
        marginTop: "16px",
        marginLeft: "32px",
        borderBottom: "1px solid #E1E1E1",
        paddingBottom: "8px"
      } as React.CSSProperties,
      topLevelToggleButton: {
        paddingBlock: 8,
        width: 170
      } as React.CSSProperties,
      nestedToggleButton: {
        paddingBlock: 6,
        paddingInline: 16,
        width: "auto",
        minWidth: 120,
        fontSize: "14px",
        fontWeight: 400,
        borderRadius: "8px"
      } as React.CSSProperties,
      familyLogoImage: { image: { height: 35, width: 35 } } as IImageStyles,
      cardHeaderDescriptionShimmerContainer: {
        root: {
          display: "grid",
          gridTemplateColumns: "24px 40%",
          gridGap: tokens.spacingVerticalSNudge,
          alignItems: "center"
        }
      } as IStackSlot,
      footerStack: { root: { alignItems: "center" } } as IStackSlot,
      exploreMoreLink: { ...typographyStyles.body1Strong, marginTop: 0 },
      aiSummaryCardStack: { root: { display: "grid", gridTemplateColumns: "auto auto 1fr auto" } } as IStackSlot,
      aiSummaryCardViewMoreButton: { root: { minHeight: 25 } } as IStackSlot,
      familyLogoWithTextStack: { root: { marginBlockEnd: tokens.spacingVerticalL } } as IStackSlot,
      currentSelectedFamily: {
        ...typographyStyles.title3,
        color: tokens.colorBrandBackground,
        fontSize: tokens.fontSizeBase600,
        fontWeight: tokens.fontWeightSemibold
      },
      currentSelectedFamilyShimmerStack: {
        inner: {
          gridRowGap: "5px",
          gridColumnGap: "16px",
          marginBlockEnd: tokens.spacingVerticalL
        }
      } as IStackSlots,
      familyToggleButtonsShimmer: {
        root: { width: 140 },
        shimmerWrapper: { borderRadius: 1000 }
      } as IStackSlots,
      exploreMoreLinkShimmer: { root: { width: "20%" } },
      productListCardsContainer: {
        inner: {
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
          paddingTop: tokens.spacingVerticalXXXL,
          paddingBottom: tokens.spacingVerticalXXXL,
          paddingLeft: tokens.spacingVerticalL,
          paddingRight: tokens.spacingVerticalL,
          gridGap: tokens.spacingVerticalL
        } as IStackSlots
      }
    }
  },
  PRODUCT_VIEW: {
    container: { paddingBottom: 10, width: "100%" } as IStyle
  },
  PRODUCT_PAGE: {
    CLASS_NAMES: mergeStyleSets({
      productPageContainer: {
        margin: 12,
        backgroundColor: "#fff"
      }
    }),
    CSS_STYLES: {
      productViewCustomBreadcrumb: {
        root: {
          fontSize: 14,
          margin: 0,
          paddingLeft: 8,
          width: 500,
          "--itemLink-color": "rgb(0, 120, 212)"
        },
        item: {
          fontSize: "inherit"
        },
        itemLink: {
          fontSize: "inherit",
          color: "var(--itemLink-color)",
          ":hover": {
            background: "none",
            textDecoration: "underline",
            color: "var(--itemLink-color)"
          }
        }
      } as Partial<IBreadcrumbStyles>
    }
  },
  OVERVIEW_SECTOIN: {
    cardContent: { padding: 0, paddingLeft: 12 } as React.CSSProperties,
    cardRoot: { maxWidth: "" } as IStyle,
    tabsCardRoot: { maxWidth: "", padding: 10, boxShadow: "var(--customShimmerShadow4)" } as IStyle,
    overviewContainer: { paddingTop: 18, paddingBottom: 18 } as React.CSSProperties,
    selectedMetricCard: { backgroundColor: "lightcyan" } as IStyle,
    shimmerStyles: {
      root: {
        gap: 13,
        height: 61,
        margin: 0,
        paddingLeft: 12,
        paddingRight: 12
      },
      articleContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-around",
        padding: 0
      },
      lineContainer: {
        backgroundColor: "inherit !important",
        margin: "0 !important"
      }
    },
    NoDataStyles: {
      display: "flex",
      alignItems: "center",
      height: 70,
      justifyContent: "center"
    } as React.CSSProperties
  },
  TABS_SECTION: {
    pivotStyles: {
      itemContainer: { paddingTop: 18, backgroundColor: "white" },
      root: { 
        height: 44, 
        backgroundColor: "white",
        borderBottom: "1px solid #edebe9"
      },
      link: {
        height: 44,
        lineHeight: "44px",
        fontSize: 14,
        fontWeight: 400,
        color: "#323130",
        padding: "0 16px",
        marginRight: 0,
        ":hover": {
          backgroundColor: "#f3f2f1",
          color: "#0078d4"
        },
        ":active": {
          backgroundColor: "#edebe9"
        }
      },
      linkIsSelected: {
        height: 44,
        lineHeight: "44px",
        fontSize: 14,
        fontWeight: 600,
        color: "#0078d4",
        padding: "0 16px",
        borderBottom: "2px solid #0078d4",
        ":hover": {
          backgroundColor: "transparent"
        }
      },
      overflowMenuButton: {
        height: 44,
        padding: "0 12px",
        color: "#323130",
        ":hover": {
          backgroundColor: "#f3f2f1",
          color: "#0078d4"
        }
      }
    } as Partial<IPivotStyles>,
    funnelContainer: {
      flexBasis: "40%",
      width: "40%"
    } as React.CSSProperties,
    funnelCard: {
      height: 400
    },
    funnelPaper: {
      borderRadius: 1,
      color: "black",
      textAlign: "center",
      fontWeight: "bold",
      fontsize: 12,
      backgroundColor: "white"
    } as React.CSSProperties
  },
  TAB: {
    SUMMARY: {
      headerStyles: {
        marginBottom: 16,
        padding: "12px 16px",
        fontSize: 18,
        fontWeight: 600,
        color: "#201F1E",
        backgroundColor: "#F3F2F1",
        borderLeft: "4px solid #0078D4",
        borderRadius: "4px",
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)"
      } as React.CSSProperties,
      stackContainer: { childrenGap: 20 } as IStackTokens,
      cardContainer: { width: "50%" } as React.CSSProperties
    },
    OPPORTUNITY_TENANTS: {
      CLASS_NAMES: mergeStyleSets({
        cardsContainer: { gap: "0 20px", display: "flex", boxSizing: "border-box", flexWrap: "wrap" } as IStyle,
        cardsContainerItem: {
          flex: 1,
          paddingBottom: 10,
          width: "calc(50% - 13px)"
        } as IStyle
      }),
      CSS_STYLES: {
        cardStyles: {
          cardDetailsContent: { padding: 13 },
          cardHeaderStart: { flex: 1 },
          cardHeaderTitleContainer: { flexBasis: "100%" },
          root: { flex: 1 }
        },
        cardHeaderShimmerStyles: {
          root: { width: "50%" },
          shimmerWrapper: {
            backgroundColor: "#deecf9",
            borderTopLeftRadius: 6,
            height: 20
          },
          shimmerGradient: {
            backgroundColor: "#deecf9",
            backgroundImage: "linear-gradient(to right, #fff0 0%, #c7e0f4 50%, #fff0 100%)"
          }
        }
      },
      messageBarStyles: { icon: { color: "var(--colorMessageBarWarningIcon)" } } as IMessageBarStyles
    },
    COHORTS: {
      CLASS_NAMES: mergeStyleSets({
        emptyDataMessageForLeftMetricDetails: {
          alignItems: "center",
          display: "flex",
          height: "calc(100% - 26px)",
          justifyContent: "center"
        },
        viewByMetricSelectContainer: {
          alignItems: "center",
          columnGap: "10px",
          display: "flex",
          flexBasis: "100%"
        } as IStyle
      })
    },
    FEATURE_VIEW: {
      CLASS_NAMES: mergeStyleSets({
        noDataForProductDisplayMessage: {
          alignItems: "center",
          display: "flex",
          fontSize: 18,
          fontWeight: 600,
          justifyContent: "center",
          minHeight: 545
        } as IStyle
      })
    },
    FEATURE_VIEW_SUMMARY_TAB: {
      CSS_STYLES: {
        customCardStyles: {
          root: { flex: 1 } as IRawStyle | undefined,
          cardDetailsContent: { padding: 13, height: 400 } as React.CSSProperties
        }
      },
      CLASS_NAMES: mergeStyleSets({
        featureViewSummaryTabContainer: {
          minHeight: 426,
          padding: 13,
          paddingTop: 10,
          width: "calc(50% - 26px)"
        } as IStyle
      })
    },
    SUPPORT_VIEW_VOLUME_TAB: {
      CSS_STYLES: {
        customCardStyles: {
          root: { display: "flex", width: "32%", height: "30%", padding: 2, boxShadow: "false" } as
            | IRawStyle
            | undefined,
          cardDetailsContent: { padding: 13, height: 350 } as React.CSSProperties
        }
      },
      CLASS_NAMES: mergeStyleSets({
        supportViewVolumeTabContainer: {
          minHeight: 426,
          padding: 13,
          paddingTop: 10,
          width: "100%-26px"
        } as IStyle
      })
    },
    DETAILED_VIEW: {
      CSS_STYLES: {
        customShimmerStyles: { root: { height: 400, width: "100%" } as IStyle },
        customCardStyles: {
          root: { marginBottom: 13 } as IRawStyle | undefined,
          cardDetailsContent: { padding: "0 13px" } as React.CSSProperties
        }
      },
      CLASS_NAMES: mergeStyleSets({
        carouselContainer: {
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          minHeight: 426,
          padding: 13,
          paddingBottom: 0,
          paddingTop: 0
        } as IStyle
      })
    },
    SEGMENTATION_TAB: {
      CSS_STYLES: {
        shimmerStyles: {
          shimmerWrapper: {
            backgroundColor: "var(--shimmer-color)",
            borderTopLeftRadius: 6,
            height: 20
          },
          shimmerGradient: {
            backgroundColor: "var(--shimmer-color)",
            backgroundImage:
              "linear-gradient(to right, rgba(255, 255, 255, 0) 0%, #c7e0f4 50%, rgba(255, 255, 255, 0) 100%)"
          }
        }
      }
    }
  }
};
